import { ChangeDetectorRef, Component, OnDestroy, OnInit, inject } from '@angular/core';
import {ChannelService, ChatClientService, StreamChatModule, StreamI18nService} from 'stream-chat-angular';
import { StreamChatService } from './stream-chat.service';
import { AuthenticationService } from '../../../../shared/services/authentication.service';
import { ActivatedRoute } from '@angular/router';
import { catchError, firstValueFrom, Observable, of, Subscription } from 'rxjs';
import * as CryptoJS from 'crypto-js';
import { ImageService } from '../../../../shared/services/image.service';
import { UnreadMessageService } from '../../../../shared/services/unread-message.service';
import { ICompany, IUser } from '../../../../../assets/types/dtoTypes';
import {
  BIPPERZ_BREAKPOINTS,
  BreakpointsService,
  MediaQueryResultMap,
} from '../../../../shared/services/breakpoint.service';
import { environment } from '../../../../environments/environment';
import {ExternalHeaderComponent} from "../../../../shared/external-header/external-header.component";
import {DatePipe, NgForOf, NgIf} from "@angular/common";
import {FooterComponent} from "../../../../shared/footer/footer.component";
import {BottomNavigationComponent} from "../../../../shared/bottom-navigation/bottom-navigation.component";
import {TranslateModule} from "@ngx-translate/core";
import {LoadMaskComponent} from "../../../../shared/load-mask/load-mask.component";


@Component({
  selector: 'app-stream-chat',
  templateUrl: './stream-chat.component.html',
  styleUrls: ['./stream-chat.component.scss'],
  standalone: true,
  imports: [
    ExternalHeaderComponent,
    DatePipe,
    FooterComponent,
    StreamChatModule,
    BottomNavigationComponent,
    TranslateModule,
    LoadMaskComponent,
    NgIf,
    NgForOf
  ]
})
export class StreamChatComponent implements OnInit, OnDestroy {
  private chatService = inject(ChatClientService);
  private unreadMessageService = inject(UnreadMessageService);
  private streamChatService = inject(StreamChatService);
  private route = inject(ActivatedRoute);
  private breakpointsService = inject(BreakpointsService);
  private channelService = inject(ChannelService);
  private authService = inject(AuthenticationService);
  private imageService = inject(ImageService);
  private streamI18nService = inject(StreamI18nService);
  private cdr = inject(ChangeDetectorRef);


  id = '';

  loggedId = '';

  channels = [];

  activeChannelId: string = '';

  isLoading = true;

  isMobile = false;

  breakpoints$: Observable<MediaQueryResultMap>;

  unreadCounts: Map<string, number> = new Map();

  isUser: boolean;

  private subscriptions: Subscription = new Subscription();


  constructor() {

    if ( this.authService.getCompanyIdFromAccessToken() ) {
      this.loggedId = this.authService.getCompanyIdFromAccessToken();
      this.isUser = false;
    } else {
      this.loggedId = this.authService.getIdFromAccessToken();
      this.isUser = true;

    }
    this.breakpointsService.observe( [ BIPPERZ_BREAKPOINTS.s, BIPPERZ_BREAKPOINTS.m_down ] ).subscribe( ( result ) => {
      this.isMobile = result.s || result.m_down;
    } );

  }

  ngOnInit(): void {
    this.route.params.subscribe( async params => {
      this.id = params.id;
      this.route.queryParams.subscribe( async () => {

        await this.initializeChat();

        const existingChannel = await this.findExistingChannel();
        const loggedInId = this.loggedId;

        if ( existingChannel.length > 0 ) {
          await this.openChannel( existingChannel[0].id );
        } else if ( this.id && this.id !== loggedInId ) {
          await this.openNewChat();
        } else {
          this.isLoading = false;
        }
      } );
    } );
  }

  private async initializeChat(): Promise<void> {
    try {

      if ( !this.chatService.chatClient?.userID ) {
        const token = await this.streamChatService.createChat( this.loggedId );
        await this.chatService.init( environment.chatApiKey, this.loggedId, token.token );
      }
      this.streamI18nService.setTranslation();



      const filters = { type: 'messaging', members: { $in: [ this.loggedId, this.id ] } };
      const options = { limit: 10, state: true };
      this.channels = await this.chatService.chatClient.queryChannels( filters, {}, options );

      this.channels = this.channels.filter( channel =>
        channel.state.members[this.loggedId] && channel.state.members[this.id],
      );


      this.channels.forEach( ( channel ) => {
        const unreadCount = channel.countUnread();
        this.unreadMessageService.updateUnreadCount( channel.id, unreadCount );

        channel.on( 'message.new', () => {
          const updatedUnreadCount = channel.countUnread();
          this.unreadMessageService.updateUnreadCount( channel.id, updatedUnreadCount );
          this.cdr.detectChanges();
        } );
      } );

      this.preloadChannelUserData();
      this.cdr.detectChanges();
    } catch ( error ) {
      console.error( 'Error initializing chat:', error );
    }
  }

  async openNewChat(): Promise<void> {
    try {



      await this.createMissingUsers( [ this.loggedId, this.id ] );

      const channelId = this.generateChannelId( this.loggedId, this.id );

      const channel = this.chatService.chatClient.channel( 'messaging', channelId, {
        members: [ this.loggedId, this.id ],
      } );

      await channel.create();
      await this.refreshChannels();
      await this.moveChannelToTop( channel.id );
      await this.openChannel( channel.id );
    } catch ( error ) {
      console.error( 'Error creating new chat:', error );
    }
  }



  generateChannelId( userId1: string, userId2: string ): string {
    return CryptoJS.SHA256( `${userId1}-${userId2}` ).toString( CryptoJS.enc.Hex ).substring( 0, 63 );
  }

  async openChannel( channelId: string ): Promise<void> {
    if ( this.activeChannelId === channelId ) return;

    this.activeChannelId = channelId;
    try {
      const channel = this.chatService.chatClient.channel( 'messaging', channelId );
      await channel.watch();
      this.channelService.setAsActiveChannel( channel );
      await this.moveChannelToTop( channelId );

      this.isLoading = false;
      this.cdr.detectChanges();
    } catch ( error ) {
      console.error( 'Error opening channel:', error );
    }
  }


  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }




  private async moveChannelToTop( channelId: string ): Promise<void> {
    const channelIndex = this.channels.findIndex( channel => channel.id === channelId );
    if ( channelIndex > -1 ) {
      const [ channel ] = this.channels.splice( channelIndex, 1 );
      this.channels.unshift( channel );
    }
  }

  private sortChannelsByRecentActivity(): void {
    this.channels.sort( ( a, b ) => {
      const aDate = new Date( a.data.last_message_at || a.data.created_at ).getTime();
      const bDate = new Date( b.data.last_message_at || b.data.created_at ).getTime();
      return bDate - aDate;
    } );
  }



  private async preloadChannelUserData(): Promise<void> {
    const loadPromises = this.channels.map( async ( channel ) => {
      const members: { user: IUser }[] = Object.values( channel.state.members );
      const otherUserId = members.find( member => member.user.id !== this.loggedId )?.user.id || null;
      if ( otherUserId ) {
        const userOrCompany = await this.resolveUserOrCompany( otherUserId );

        if ( this.isCompany( userOrCompany ) ) {
          const updatedImage = await this.imageService.appendSasTokenToCompanyImage( userOrCompany.logoUrl, userOrCompany.id, true ).toPromise();
          channel.otherUserImage = updatedImage || 'path_to_default_avatar_image.jpg';
          channel.otherUserName = userOrCompany.name || 'Unknown Company';
        } else {
          const updatedUser = await this.imageService.appendSasTokenToProfileImage( userOrCompany ).toPromise();
          channel.otherUserImage = updatedUser.profileImageUrl || 'path_to_default_avatar_image.jpg';
          channel.otherUserName = updatedUser.firstname || 'Unknown User';
        }
      }
    } );

    await Promise.all( loadPromises );
    this.cdr.detectChanges();
  }

  private isCompany( entity: IUser | ICompany ): entity is ICompany {
    return ( entity as ICompany ).industry !== undefined;
  }


  private async refreshChannels(): Promise<void> {
    const filters = { type: 'messaging', members: { $in: [ this.loggedId ] } };
    const options = { limit: 10, state: true };
    this.channels = await this.chatService.chatClient.queryChannels( filters, {}, options );
    this.sortChannelsByRecentActivity();
  }

  private async createMissingUsers( userIds: string[] ): Promise<void> {
    if ( !this.chatService.chatClient ) {
      console.error( 'Chat client is not initialized.' );
      return;
    }

    try {
      const users = userIds.map( id => ( {
        id,
        name: `User ${id}`,
        image: 'default_image_url',
      } ) );

      await this.chatService.chatClient.upsertUsers( users );
    } catch ( error ) {
      console.error( 'Error creating or updating users:', error );
    }
  }

  private async findExistingChannel(): Promise<{ id: string }[]> {
    const firstFilter = {
      type: 'messaging',
      members: { $in: [ this.loggedId ] },
    };
    const userChannels = await this.chatService.chatClient.queryChannels( firstFilter );


    const secondFilter = { type: 'messaging', members: { $in: [ this.id ] } };

    const newUserChannels = await this.chatService.chatClient.queryChannels( secondFilter );

    return userChannels.filter( channel1 =>
      newUserChannels.some( channel2 => channel1.id === channel2.id ),
    );
  }

  async resolveUserOrCompany( userId: string ): Promise<IUser | ICompany> {
    try {
      const user = await this.getUserData( userId );
      if ( user ) return user;
    } catch { /* empty */ }

    try {
      const company = await firstValueFrom( this.getCompanyData( userId ).pipe( catchError( () => of( null ) ) ) );
      if ( company ) return company;
    } catch { /* empty */ }

    return { firstname: 'Unknown User', profileImageUrl: 'path_to_default_avatar_image.jpg' } as IUser;
  }

  private async getUserData( userId: string ): Promise<IUser> {
    return this.streamChatService.getUser( userId );
  }

  private getCompanyData( companyId: string ): Observable<ICompany> {
    return this.streamChatService.getCompanyData( companyId );
  }
}
